import styled from 'styled-components';
import { monserratSemiBold, robotoRegular } from '@habitech/shared';

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f3f4f6;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Navbar
// ─────────────────────────────────────────────────────────────────────────────
export const NavBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 0.875rem 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

export const NavBrand = styled.div``;

export const NavTitle = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #5b21b6;
  font-family: ${monserratSemiBold};
`;

export const NavSubtitle = styled.p`
  margin: 0;
  font-size: 12px;
  color: #9ca3af;
  font-family: ${robotoRegular};

  span.dot {
    margin: 0 6px;
    color: #d1d5db;
  }

  span.email {
    color: #6b7280;
  }
`;

export const NavCenter = styled.div`
  display: flex;
  padding: 0 1.5rem 0 2rem;
  width: 480px;
  flex-shrink: 0;
`;

export const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
`;

export const NavEmail = styled.span`
  font-size: 12px;
  color: #6b7280;
  font-family: ${robotoRegular};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`;

export const PageFooter = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fffbeb;
  border-top: 1px solid #fde68a;
  color: #92400e;
  font-size: 12px;
  font-family: ${robotoRegular};
  padding: 6px 2rem;
  text-align: center;
  z-index: 90;
`;

export const EnvBadge = styled.span<{ $color: string }>`
  background: ${({ $color }) => $color + '15'};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) => $color + '35'};
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  font-family: ${robotoRegular};
`;

export const LogoutButton = styled.button`
  background: transparent;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 12px;
  color: #6b7280;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  font-family: ${robotoRegular};

  &:hover {
    border-color: #dc2626;
    color: #dc2626;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Search bar
// ─────────────────────────────────────────────────────────────────────────────
export const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
`;

export const SearchInputBox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.625rem 1rem;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus-within {
    border-color: #9ca3af;
    box-shadow: 0 0 0 3px rgba(156, 163, 175, 0.15);
  }
`;

export const SearchIconWrapper = styled.span`
  color: #9ca3af;
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

export const SearchInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font-size: 14px;
  color: #1f2937;
  background: transparent;
  font-family: ${robotoRegular};

  &::placeholder {
    color: #9ca3af;
  }
`;

export const SearchDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18), 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 50;
  overflow: hidden;
`;

export const SearchDropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid #f9fafb;
  transition: background 0.1s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #faf9ff;
  }
`;

export const SearchDropdownName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
  font-family: ${robotoRegular};
  flex: 1;
`;

export const SearchDropdownDescription = styled.span`
  font-size: 12px;
  color: #9ca3af;
  font-family: ${robotoRegular};
`;

export const SearchEmpty = styled.div`
  padding: 1.25rem 1rem;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
  font-family: ${robotoRegular};
`;

// ─────────────────────────────────────────────────────────────────────────────
// Panel — contenedor tipo AWS widget
// ─────────────────────────────────────────────────────────────────────────────
export const Panel = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  transition: height 0.25s ease;
`;

export const PanelHeader = styled.div`
  padding: 0.875rem 1.25rem;
  border-bottom: 1px solid #f3f4f6;
  border-radius: 16px 16px 0 0;
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #1f2937;
  font-family: ${monserratSemiBold};
`;

export const PanelSubtitle = styled.p`
  margin: 0.1rem 0 0;
  font-size: 11px;
  color: #9ca3af;
  font-family: ${robotoRegular};
`;

// ─────────────────────────────────────────────────────────────────────────────
// List container — área scrolleable dentro del panel
// ─────────────────────────────────────────────────────────────────────────────
export const ListContainer = styled.div`
  flex: 1;
  overflow-y: hidden;
  &[data-scrollable='true'] {
    overflow-y: auto;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// List item — fila clickeable estilo AWS
// ─────────────────────────────────────────────────────────────────────────────
export const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1.25rem;
  cursor: pointer;
  border-bottom: 1px solid #f9fafb;
  transition: background 0.1s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #faf9ff;
  }
`;

export const ListItemIcon = styled.div<{ $color?: string }>`
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 6px;
  background: ${({ $color }) => ($color ? $color + '15' : '#e5e7eb')};
  border: 1px solid ${({ $color }) => ($color ? $color + '28' : '#d1d5db')};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $color }) => $color ?? '#7c3aed'};
  flex-shrink: 0;
`;

export const ListItemName = styled.span`
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
  font-family: ${robotoRegular};
  flex: 1;
`;

export const ListItemDescription = styled.span`
  font-size: 12px;
  color: #9ca3af;
  font-family: ${robotoRegular};
`;

// ─────────────────────────────────────────────────────────────────────────────
// Panel footer — "Ver todos / Ver menos"
// ─────────────────────────────────────────────────────────────────────────────
export const PanelFooter = styled.button`
  width: 100%;
  background: none;
  border: none;
  border-top: 1px solid #f3f4f6;
  border-radius: 0 0 16px 16px;
  padding: 0.625rem 1.25rem;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #7c3aed;
  cursor: pointer;
  font-family: ${robotoRegular};
  transition: background 0.1s;
  margin-top: auto;

  &:hover {
    background: #faf9ff;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// SSO badge — inline junto al nombre
// ─────────────────────────────────────────────────────────────────────────────
export const SsoBadge = styled.span`
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
  font-family: ${robotoRegular};
  white-space: nowrap;
  flex-shrink: 0;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Estado "abriendo herramienta" — inline en la fila
// ─────────────────────────────────────────────────────────────────────────────
export const OpeningRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1.25rem;
  border-bottom: 1px solid #f9fafb;
  color: #5b21b6;
  font-size: 13px;
  font-family: ${monserratSemiBold};

  &:last-child {
    border-bottom: none;
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Banner localhost
// ─────────────────────────────────────────────────────────────────────────────
export const LocalhostBanner = styled.div`
  background: #fffbeb;
  border-bottom: 1px solid #fcd34d;
  padding: 7px 1.25rem;
  font-size: 12px;
  color: #92400e;
  font-family: ${robotoRegular};

  strong {
    font-family: ${monserratSemiBold};
  }
`;
